/**
 * WeekShapeStrip — Growth Engine 2b (Feb 17, 2026).
 *
 * Calm, READ-ONLY operational X-ray of the instructor's week.
 * Mounts on the ADI dashboard between the Work Mode card and the
 * onboarding/learner sections. NO writes. NO notifications. NO
 * "AI optimisation" wording. Strict cap of 2 observations.
 *
 * Governed by /app/memory/GROWTH_DOCTRINE.md. Suppressed entirely
 * when /api/opportunities/week-shape returns observations=[]
 * (calm-baseline silence — §"Operational trust principles").
 *
 * Reuses existing <MobileBottomSheet> primitive for the full-week
 * detail panel on mobile; renders the same panel as a right-rail
 * slide-over on desktop (≥md).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarRange, ChevronRight } from "lucide-react";

import WeekShapeSheet from "./WeekShapeSheet";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WeekShapeStrip() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/opportunities/week-shape`, {
        withCredentials: true,
      });
      setData(r.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Calm-baseline silence — render nothing if API errored, or if
  // prompt_level=never, or if the week is healthy enough that no
  // observations were emitted.
  if (!data) return null;
  if ((data.prompt_level || "important") === "never") return null;
  const obs = data.observations || [];
  if (obs.length === 0) return null;

  const total = data.totals?.confirmed_lessons ?? 0;

  return (
    <>
      <section
        data-testid="week-shape-strip"
        className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
      >
        <header className="flex items-center justify-between gap-3 mb-2.5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
              <CalendarRange className="w-4 h-4 text-slate-600 dark:text-slate-300" />
            </div>
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              This week's shape
            </p>
          </div>
          <p
            data-testid="week-shape-strip-total"
            className="text-[11px] text-slate-500 dark:text-slate-400"
          >
            {total} confirmed lessons
          </p>
        </header>

        <ul className="space-y-1.5 mb-2">
          {obs.slice(0, 2).map((line, i) => (
            <li
              key={i}
              data-testid={`week-shape-observation-${i}`}
              className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed"
            >
              {line}
            </li>
          ))}
        </ul>

        <button
          type="button"
          data-testid="week-shape-full-week-link"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 text-[12px] font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          See full week
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </section>

      <WeekShapeSheet open={open} onClose={() => setOpen(false)} data={data} />
    </>
  );
}
